// src/context/brandVoice/wireShape.js — the EDITOR's shape of a field vs the shape the API stores. PURE.
//
// Most widgets edit exactly what the API stores, so both converters are the identity for
// them. ⭐ `pair_list` (only `communication_principles`, the "Principi" tab) is the
// exception: the API stores ONE string per principle, "Nome: descrizione", and the editor
// shows TWO boxes. The draft holds the ROWS `{ name, description }` (decision 2,
// 2026-09-27): a controlled editor over the joined string would re-split on every
// keystroke — the rest of a name would jump into the description box as soon as its
// colon-space was typed, and a trimmed join would eat the space the user just typed.
//
// ⛔⛔ The split rule is exact, and the backend cannot detect a wrong one (no 422: the
// boundary silently moves on the next load — 12 descriptions were once lost that way):
//   split on the FIRST colon-space; everything after it is the description;
//   none → name only;
//   join with exactly one colon-space, BOTH parts trimmed; an empty description → the
//   name ALONE (a trailing separator would come back inside the name box).
// ⭐ A row whose two boxes are BOTH blank is DROPPED by `toWire` (decision A): an added row
// the user never filled is not a change and is never sent. A row with a description but
// no name joins to a leading separator — which splits back to the same row — and
// `validateField` refuses it on save.
// Both converters accept EITHER shape and are idempotent: `canonical` compares a stored
// value (strings) with a draft (rows).
//
// ⭐ v186: a `str_list` (a flat list of sentences) has the same shape in the editor and on
// the wire, but a BLANK entry is dropped here too (decision 5, 2026-09-28): an added row
// the user never filled is not a change and is never sent. The backend refuses only blank
// ENTRIES, so a list of non-blank entries — `[]` included, a clear — is always accepted.
//
// ⭐ v187: a `record_list` row (terminology, taboo claims) keeps its shape too, but before it
// leaves the editor every EMPTY value is stripped (the contract reads `""` / `null` as
// absent; sending only real values keeps the backend's text surgery free of doubt) and a
// row left with nothing is DROPPED — an added row nobody filled is not a change.
// ⭐ v188: the same record rule applies inside each claims category.
//
// ⛔ Zero imports, and deliberately NOT in `widgets/index.js` (which pulls React in): the
// pure modules (`fieldValue`, `validateField`, `draftModel`) depend on these converters.
export const PAIR_SEPARATOR = ': ';

const str = (v) => (typeof v === 'string' ? v : '');
const isRow = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

const blankValue = (v) => v == null || (typeof v === 'string' && !v.trim());
/** A record without its empty values ("" / null / whitespace). */
const withoutBlanks = (r) => Object.fromEntries(Object.entries(r).filter(([, v]) => !blankValue(v)));

/** A list of records without empty values, and without rows left holding nothing.
 *  A NON-object entry passes through untouched, so `validateField` still answers `rowShape`. */
const recordRows = (rows) => rows
  .map((r) => (isRow(r) ? withoutBlanks(r) : r))
  .filter((r) => !(isRow(r) && Object.keys(r).length === 0));

/** One stored entry → { name, description }. Never throws. */
export function splitPair(entry) {
  const s = str(entry);
  const at = s.indexOf(PAIR_SEPARATOR);
  return at === -1
    ? { name: s, description: '' }
    : { name: s.slice(0, at), description: s.slice(at + PAIR_SEPARATOR.length) };
}

/** { name, description } → the stored string, or null for a row with both boxes blank. */
export function joinPair(row) {
  const name = str(row?.name).trim();
  const description = str(row?.description).trim();
  if (!name && !description) return null;
  return description ? `${name}${PAIR_SEPARATOR}${description}` : name;
}

/** The stored value → the editor's value (identity except for pair_list). */
export function fromWire(field, value) {
  if (field?.widget !== 'pair_list') return value;
  return (Array.isArray(value) ? value : []).map((e) => (isRow(e) ? e : splitPair(e)));
}

/** The editor's value → what the PUT sends (pair_list rows joined; blank entries / rows dropped). */
export function toWire(field, value) {
  if (field?.widget === 'claims') {
    // ⭐ v188: `verified_claims` is `{category: record[]}` — the record rule, per category.
    // ⛔ This branch MUST stay above the `!Array.isArray` return: a claims value is an
    // OBJECT and would otherwise come back untouched.
    if (!isRow(value)) return value;
    return Object.fromEntries(Object.entries(value)
      .map(([category, rows]) => [category, Array.isArray(rows) ? recordRows(rows) : rows]));
  }
  if (!Array.isArray(value)) return value;
  if (field?.widget === 'pair_list') {
    return value.map((e) => (isRow(e) ? joinPair(e) : e)).filter((e) => e !== null);
  }
  if (field?.widget === 'str_list') {
    return value.filter((e) => !(typeof e === 'string' && !e.trim()));
  }
  if (field?.widget === 'record_list') return recordRows(value);
  return value;
}
